import React, { useState, useEffect } from 'react';
import { loadWallet, generateWallet, saveWallet } from './wallet';
import Verify from './Verify';
import FounderLogin from './FounderLogin';
import './index.css';

const API_URL = 'https://aether-api.whiteghostpheonix.workers.dev';

function App() {
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState(0);
  const [balanceDisplay, setBalanceDisplay] = useState('0');
  const [isInfinite, setIsInfinite] = useState(false);
  const [isFounder, setIsFounder] = useState(false);
  const [verified, setVerified] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [showFounderLogin, setShowFounderLogin] = useState(false);
  const [toAddress, setToAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check for founder login page request
    if (window.location.pathname === '/founder' || window.location.hash === '#founder') {
      setShowFounderLogin(true);
      return;
    }

    const w = loadWallet();
    setWallet(w);

    if (w.isFounder) {
      // Founder is logged in
      setIsFounder(true);
      setVerified(true);
      setNeedsVerify(false);
      loadFounderData(w);
    } else {
      // Regular user flow
      const savedVerified = localStorage.getItem('aether_verified');
      if (savedVerified === 'true') {
        setVerified(true);
        loadUserData(w);
      } else {
        setNeedsVerify(true);
      }
    }
  }, []);

  const loadFounderData = async (w) => {
    try {
      const userRes = await fetch(`${API_URL}/user?address=${w.address}`);
      if (userRes.ok) {
        const data = await userRes.json();
        if (data.wallet) {
          setIsInfinite(true);
          setBalanceDisplay('∞');
        }
      }
      const txRes = await fetch(`${API_URL}/transactions?address=${w.address}`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.transactions || []);
      }
    } catch (err) {
      setBalanceDisplay('∞');
    }
  };

  const loadUserData = async (w) => {
    try {
      const userRes = await fetch(`${API_URL}/user?address=${w.address}`);
      if (userRes.ok) {
        const data = await userRes.json();
        if (data.wallet) {
          setIsInfinite(data.wallet.is_infinite === true);
          setBalanceDisplay(data.wallet.is_infinite ? '∞' : (data.wallet.balance || 0).toLocaleString());
        }
      }
      const txRes = await fetch(`${API_URL}/transactions?address=${w.address}`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData.transactions || []);
      }
    } catch (err) {}
  };

  const handleVerified = async (data) => {
    setVerified(true);
    setNeedsVerify(false);
    localStorage.setItem('aether_verified', 'true');
    localStorage.setItem('aether_biometric_hash', data.hash);
    localStorage.setItem('aether_method', data.method);

    try {
      const res = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: data.phone || 'device_' + wallet.address.substring(0, 10),
          address: wallet.address,
          biometric_hash: data.hash,
          method: data.method,
          trust: data.trust,
        }),
      });
      const result = await res.json();
      if (result.wallet) {
        setIsInfinite(result.wallet.is_infinite === true || result.wallet.is_infinite === 1);
        setBalanceDisplay(result.wallet.is_infinite ? '∞' : '0');
      }
      setMessage(`✅ Verified via ${data.method} (${data.trust}%)`);
      setMessageType('success');
    } catch (err) {
      setMessage('❌ Registration failed');
      setMessageType('error');
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(wallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendTransaction = async () => {
    if (!toAddress || !amount) {
      setMessage('❌ Fill in all fields');
      setMessageType('error');
      return;
    }
    setLoading(true);
    setMessage('⏳ Processing...');

    try {
      const response = await fetch(`${API_URL}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: wallet.address,
          to: toAddress,
          amount: parseInt(amount),
        }),
      });
      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ Sent ${amount} AETH! Gas: 0`);
        setMessageType('success');
        setTransactions([{ to_address: toAddress, amount: parseInt(amount), timestamp: Math.floor(Date.now() / 1000) }, ...transactions]);
        setToAddress('');
        setAmount('');
        if (!isInfinite && !isFounder) {
          const newBalance = Math.max(0, balance - parseInt(amount));
          setBalance(newBalance);
          setBalanceDisplay(newBalance.toLocaleString());
        }
      } else {
        setMessage(`❌ ${data.error || 'Failed'}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage(`❌ ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const logoutFounder = () => {
    localStorage.removeItem('aether_wallet');
    localStorage.removeItem('aether_verified');
    window.location.href = '/';
  };

  const clearAndStartFresh = () => {
    localStorage.removeItem('aether_wallet');
    localStorage.removeItem('aether_verified');
    const newWallet = generateWallet();
    saveWallet(newWallet);
    window.location.reload();
  };

  if (!wallet && !showFounderLogin) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#030014', color: 'white' }}>Loading...</div>;

  // FOUNDER LOGIN PAGE
  if (showFounderLogin) {
    return <FounderLogin />;
  }

  // BIOMETRIC VERIFY SCREEN (regular users)
  if (needsVerify && !verified) {
    return (
      <div className="app">
        <div className="app-bg">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
        </div>
        <header className="header">
          <div className="logo">
            <div className="logo-icon">⚡</div>
            <div className="logo-text">
              <div className="logo-title">AETHER</div>
              <div className="logo-subtitle">Verify to Begin</div>
            </div>
          </div>
          <div className="header-badge">STEP 1/2</div>
        </header>
        <div className="container">
          <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
            <h2 className="card-title" style={{ fontSize: '22px' }}>Verify Identity First</h2>
            <p className="card-subtitle">One biometric = One account</p>
          </div>
          <Verify wallet={wallet} onVerified={handleVerified} />
          
          <div className="card" style={{ textAlign: 'center', marginTop: '16px' }}>
            <p style={{ color: '#FFB800', fontSize: '13px', marginBottom: '12px' }}>
              👑 Founder?
            </p>
            <a href="/founder" style={{ color: '#FFB800', fontWeight: 'bold', textDecoration: 'underline' }}>
              Login as Founder →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // MAIN WALLET
  return (
    <div className="app">
      <div className="app-bg">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>

      <header className="header">
        <div className="logo">
          <div className="logo-icon">⚡</div>
          <div className="logo-text">
            <div className="logo-title">AETHER</div>
            <div className="logo-subtitle">Gas-Free Blockchain</div>
          </div>
        </div>
        <div className="header-badge" style={isFounder ? { background: 'linear-gradient(135deg, #FFB800, #CC9200)' } : {}}>
          {isFounder ? '👑 FOUNDER' : '✓ VERIFIED'}
        </div>
      </header>

      <div className="container">
        <div className="card balance-card" style={isFounder ? { borderColor: '#FFB800', boxShadow: '0 0 40px rgba(255, 184, 0, 0.3)' } : {}}>
          <div className="balance-header">
            <span className="balance-label">{isFounder ? 'Founder Balance' : 'Total Balance'}</span>
          </div>
          <div className="balance-amount" style={isFounder ? { background: 'linear-gradient(135deg, #FFB800, #CC9200)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } : {}}>
            {balanceDisplay}
            <span className="balance-currency">AETH</span>
          </div>
          {isFounder && (
            <p style={{ color: '#FFB800', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
              👑 Unlimited balance — hidden from other users
            </p>
          )}
          <div className="address-box">
            <div className="address-icon">👤</div>
            <div className="address-text">{wallet.address}</div>
            <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={copyAddress}>
              {copied ? '✓' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Send AETH</h3>
          <p className="card-subtitle">Transfer instantly with zero fees</p>
          <div className="gas-pill">Gas: 0 AETH</div>

          <div className="input-group">
            <label className="input-label">Recipient</label>
            <input type="text" className="input" placeholder="0x..." value={toAddress} onChange={(e) => setToAddress(e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Amount</label>
            <input type="number" className="input" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <button className="send-btn" onClick={sendTransaction} disabled={loading}>
            {loading ? '⏳ Processing...' : '🚀 Send Now'}
          </button>
          {message && <div className={`message ${messageType}`}>{message}</div>}
        </div>

        <div className="card">
          <h3 className="card-title">Recent Activity</h3>
          {transactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <div className="empty-text">No transactions yet</div>
            </div>
          ) : (
            <div className="tx-list">
              {transactions.slice(0, 5).map((tx, i) => (
                <div key={i} className="tx-item">
                  <div className="tx-icon">🚀</div>
                  <div className="tx-details">
                    <div className="tx-address">→ {tx.to_address?.slice(0, 16)}...</div>
                    <div className="tx-time">{tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleString() : 'Recently'}</div>
                  </div>
                  <div className="tx-amount">-{tx.amount} AETH</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <button onClick={clearAndStartFresh} style={{ background: 'rgba(96, 165, 250, 0.1)', color: '#60a5fa', border: '1px solid rgba(96, 165, 250, 0.3)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', marginRight: '8px' }}>
            ✨ New Wallet
          </button>
          {isFounder && (
            <button onClick={logoutFounder} style={{ background: 'rgba(255, 184, 0, 0.1)', color: '#FFB800', border: '1px solid rgba(255, 184, 0, 0.3)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}>
              🚪 Logout Founder
            </button>
          )}
        </div>

        <footer className="footer">
          <div className="footer-brand">⚡ AETHER</div>
          <p>Built to be FREE. Forever.</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
